import { useState } from "react";
import { 
  X, ArrowRight, ArrowLeft, CheckCircle, AlertTriangle, XCircle, 
  Upload, FileText, Info, Target, Lightbulb
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const GapIdentifier = ({ onClose }) => {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    certification_target: "",
    gap_categories: [],
    audit_findings: "",
    self_identified_gaps: []
  });

  const certifications = [
    { id: "iso9001", name: "ISO 9001 - Quality Management" },
    { id: "iso22000", name: "ISO 22000 - Food Safety" },
    { id: "fssc22000", name: "FSSC 22000 - Food Safety Certification" },
    { id: "haccp", name: "HACCP" },
    { id: "iso27001", name: "ISO 27001 - Information Security" },
    { id: "iso45001", name: "ISO 45001 - Health & Safety" },
    { id: "globalgap", name: "GlobalG.A.P." }
  ];

  const gapCategories = [
    { id: "documentation", name: "Documentation & Records", desc: "Policies, procedures, work instructions, records", severity: "high" },
    { id: "training", name: "Training & Competence", desc: "Staff training, awareness, competency records", severity: "medium" },
    { id: "monitoring", name: "Monitoring & Measurement", desc: "KPIs, audits, inspections, testing", severity: "high" },
    { id: "management", name: "Management Commitment", desc: "Leadership, resources, policy, reviews", severity: "medium" },
    { id: "risk", name: "Risk Assessment", desc: "Hazard analysis, risk evaluation, controls", severity: "high" },
    { id: "corrective", name: "Corrective Actions", desc: "Nonconformance handling, root cause, CAPA", severity: "high" },
    { id: "supplier", name: "Supplier Management", desc: "Supplier evaluation, approved suppliers, audits", severity: "medium" },
    { id: "infrastructure", name: "Infrastructure & Equipment", desc: "Facilities, equipment, maintenance, calibration", severity: "medium" },
    { id: "communication", name: "Communication", desc: "Internal/external communication, awareness", severity: "low" },
    { id: "emergency", name: "Emergency Preparedness", desc: "Emergency plans, drills, response procedures", severity: "medium" }
  ];

  const commonGapsByCategory = {
    documentation: [
      "Missing or outdated procedures",
      "No document control system",
      "Incomplete records",
      "No retention policy"
    ],
    training: [
      "No training matrix",
      "Missing competency assessments",
      "Insufficient awareness training",
      "No training records"
    ],
    monitoring: [
      "Inadequate internal audit program",
      "Missing KPIs",
      "Incomplete inspection records",
      "No trend analysis"
    ],
    management: [
      "No management review process",
      "Inadequate resource allocation",
      "Policy not communicated",
      "Objectives not defined"
    ],
    risk: [
      "Incomplete hazard analysis",
      "No risk register",
      "Missing preventive controls",
      "Risk assessment not updated"
    ],
    corrective: [
      "No CAPA procedure",
      "Root cause not addressed",
      "Incomplete corrective actions",
      "No effectiveness verification"
    ],
    supplier: [
      "No approved supplier list",
      "Missing supplier audits",
      "Incomplete specifications",
      "No performance monitoring"
    ],
    infrastructure: [
      "Inadequate maintenance program",
      "Missing calibration records",
      "Facility hygiene issues",
      "Equipment validation gaps"
    ],
    communication: [
      "No communication procedure",
      "Inadequate incident reporting",
      "External communication gaps",
      "No feedback mechanism"
    ],
    emergency: [
      "No emergency response plan",
      "Missing emergency drills",
      "Incomplete contact lists",
      "No recovery procedures"
    ]
  };

  const rootCauseCategories = {
    people: ["Inadequate training", "Staff turnover", "Lack of awareness", "Insufficient resources"],
    process: ["No defined process", "Process not followed", "Unclear responsibilities", "Missing validation"],
    technology: ["Outdated systems", "No automation", "Inadequate tools", "Integration issues"],
    management: ["Lack of commitment", "Inadequate oversight", "Poor communication", "Resource constraints"]
  };

  const toggleCategory = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      gap_categories: prev.gap_categories.includes(categoryId)
        ? prev.gap_categories.filter(c => c !== categoryId)
        : [...prev.gap_categories, categoryId]
    }));
  };

  const toggleGap = (gap) => {
    setFormData(prev => ({
      ...prev,
      self_identified_gaps: prev.self_identified_gaps.includes(gap)
        ? prev.self_identified_gaps.filter(g => g !== gap)
        : [...prev.self_identified_gaps, gap]
    }));
  };

  const analyzeGaps = () => {
    setIsLoading(true);
    
    // Simulate analysis (in production, this would call an API)
    setTimeout(async () => {
      const selectedCategories = gapCategories.filter(c => 
        formData.gap_categories.includes(c.id)
      );
      
      const gaps = [];
      const recommendations = [];
      
      selectedCategories.forEach(cat => {
        const categoryGaps = commonGapsByCategory[cat.id] || [];
        categoryGaps.forEach(gap => {
          gaps.push({
            category: cat.name,
            gap: gap,
            severity: cat.severity,
            root_causes: rootCauseCategories.process.slice(0, 2)
          });
        });
        
        // Add recommendations
        recommendations.push({
          category: cat.name,
          priority: cat.severity,
          action: `Address ${cat.name.toLowerCase()} gaps through systematic review and improvement`,
          timeframe: cat.severity === 'high' ? '1-3 months' : '3-6 months'
        });
      });

      // Add self-identified gaps
      formData.self_identified_gaps.forEach(gap => {
        gaps.push({
          category: "Self-Identified",
          gap: gap,
          severity: "medium",
          root_causes: ["Review required to determine root cause"]
        });
      });

      const analysisResult = {
        id: `gap-${Date.now()}`,
        certification_target: formData.certification_target,
        total_gaps: gaps.length,
        high_priority: gaps.filter(g => g.severity === 'high').length,
        medium_priority: gaps.filter(g => g.severity === 'medium').length,
        low_priority: gaps.filter(g => g.severity === 'low').length,
        gaps: gaps.slice(0, 12),
        recommendations: recommendations,
        next_steps: [
          "Prioritize high-severity gaps for immediate action",
          "Develop a corrective action plan with timelines",
          "Assign responsibilities for each gap",
          "Consider a formal gap assessment with our consultants"
        ]
      };

      // Save to session
      const sessionId = localStorage.getItem("chatbot_session_id");
      if (sessionId) {
        try {
          await axios.post(`${API}/tools/session/save`, {
            session_id: sessionId,
            tool_name: "gap_identifier",
            result: analysisResult
          });
        } catch (e) {
          console.error("Failed to save session:", e);
        }
      }

      setResult(analysisResult);
      setIsLoading(false);
    }, 1500);
  };

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case "high": return "bg-red-100 text-red-700";
      case "medium": return "bg-amber-100 text-amber-700";
      case "low": return "bg-green-100 text-green-700";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">What certification are you assessing?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Select the standard you&apos;re working towards or maintaining.
              </p>
            </div>
            <div className="space-y-2">
              {certifications.map((cert) => (
                <button
                  key={cert.id}
                  onClick={() => { setFormData({ ...formData, certification_target: cert.id }); setStep(2); }}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all hover:border-brand ${
                    formData.certification_target === cert.id ? 'border-brand bg-brand/5' : 'border-slate-200'
                  }`}
                >
                  <span className="font-medium">{cert.name}</span>
                </button>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Which areas have gaps?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Select all categories where you&apos;ve identified or suspect gaps.
              </p>
            </div>
            <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${errors.gap_categories ? 'ring-2 ring-red-300 ring-offset-2 rounded-xl' : ''}`}>
              {gapCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => {
                    toggleCategory(cat.id);
                    if (errors.gap_categories) setErrors({...errors, gap_categories: null});
                  }}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    formData.gap_categories.includes(cat.id) 
                      ? 'border-brand bg-brand/5' 
                      : 'border-slate-200 hover:border-brand/50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium text-sm">{cat.name}</div>
                      <div className="text-xs text-muted-foreground mt-1">{cat.desc}</div>
                    </div>
                    <Badge className={`${getSeverityBadge(cat.severity)} text-xs`}>
                      {cat.severity}
                    </Badge>
                  </div>
                </button>
              ))}
            </div>
            {errors.gap_categories && (
              <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg" data-testid="error-gap-categories">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>{errors.gap_categories}</span>
              </div>
            )}
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button 
                onClick={() => {
                  if (formData.gap_categories.length === 0) {
                    setErrors({...errors, gap_categories: "Please select at least one gap category to continue"});
                    return;
                  }
                  setStep(3);
                }} 
                className="bg-brand hover:bg-brand-dark"
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Specific Gaps (Optional)</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Select any specific gaps you&apos;ve already identified, or paste audit findings below.
              </p>
            </div>
            
            {/* Quick select common gaps */}
            <div className="space-y-4">
              {formData.gap_categories.slice(0, 3).map(catId => {
                const category = gapCategories.find(c => c.id === catId);
                const gaps = commonGapsByCategory[catId] || [];
                return (
                  <div key={catId}>
                    <Label className="text-sm font-medium mb-2 block">{category?.name}</Label>
                    <div className="flex flex-wrap gap-2">
                      {gaps.map((gap) => (
                        <button
                          key={gap}
                          onClick={() => toggleGap(gap)}
                          className={`px-3 py-1.5 rounded-full border text-xs transition-all ${
                            formData.self_identified_gaps.includes(gap)
                              ? 'bg-brand text-white border-brand'
                              : 'bg-white border-slate-200 hover:border-brand'
                          }`}
                        >
                          {gap}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Audit findings text area */}
            <div>
              <Label htmlFor="audit_findings">Paste Audit Findings (Optional)</Label>
              <Textarea
                id="audit_findings"
                value={formData.audit_findings}
                onChange={(e) => setFormData({ ...formData, audit_findings: e.target.value })}
                className="mt-1 min-h-[100px]"
                placeholder="Paste any existing audit findings or nonconformances here..."
              />
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button 
                onClick={analyzeGaps} 
                className="bg-brand hover:bg-brand-dark"
                disabled={isLoading}
              >
                {isLoading ? "Analyzing..." : "Analyze Gaps"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (result) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-primary">Gap Analysis Results</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-slate-50 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold">{result.total_gaps}</p>
            <p className="text-xs text-muted-foreground">Total Gaps</p>
          </div>
          <div className="bg-red-50 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-red-600">{result.high_priority}</p>
            <p className="text-xs text-red-600">High Priority</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-amber-600">{result.medium_priority}</p>
            <p className="text-xs text-amber-600">Medium</p>
          </div>
          <div className="bg-green-50 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-green-600">{result.low_priority}</p>
            <p className="text-xs text-green-600">Low</p>
          </div>
        </div>

        {/* Gaps List */}
        <div className="mb-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Identified Gaps
          </h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {result.gaps.map((gap, index) => (
              <div key={index} className="bg-slate-50 rounded-lg p-3 flex items-start justify-between">
                <div>
                  <p className="font-medium text-sm">{gap.gap}</p>
                  <p className="text-xs text-muted-foreground">{gap.category}</p>
                </div>
                <Badge className={getSeverityBadge(gap.severity)}>{gap.severity}</Badge>
              </div>
            ))}
          </div>
        </div>

        {/* Recommendations */}
        <div className="bg-brand/5 border border-brand/20 rounded-xl p-4 mb-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-brand" />
            Recommendations
          </h3>
          <div className="space-y-3">
            {result.recommendations.slice(0, 4).map((rec, index) => (
              <div key={index} className="flex items-start gap-3">
                <Badge className={getSeverityBadge(rec.priority)} variant="outline">
                  {rec.timeframe}
                </Badge>
                <div>
                  <p className="text-sm font-medium">{rec.category}</p>
                  <p className="text-xs text-muted-foreground">{rec.action}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-slate-50 rounded-xl p-4 mb-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Target className="h-4 w-4 text-brand" />
            Next Steps
          </h3>
          <ul className="space-y-2">
            {result.next_steps.map((step, i) => (
              <li key={i} className="text-sm flex items-start gap-2">
                <span className="font-bold text-brand">{i + 1}.</span> {step}
              </li>
            ))}
          </ul>
        </div>

        {/* Disclaimer */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
          <p className="text-xs text-amber-700 flex items-start gap-2">
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
            This analysis provides high-level guidance based on common gaps and is not an official audit. 
            All findings should be verified through professional assessment.
          </p>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={() => { setResult(null); setStep(1); }} className="flex-1">
            Start Over
          </Button>
          <Button onClick={onClose} className="flex-1 bg-brand hover:bg-brand-dark">
            Request Expert Assessment
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-primary">Audit Gap Identifier</h2>
          <p className="text-sm text-muted-foreground">Step {step} of 3</p>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-slate-100 rounded-full h-2 mb-8">
        <div 
          className="bg-brand h-2 rounded-full transition-all duration-300"
          style={{ width: `${(step / 3) * 100}%` }}
        />
      </div>

      {renderStep()}
    </div>
  );
};

export default GapIdentifier;
